import { Button } from 'src/v2/commonComponents/Button';
import styles from './CreditsSection.module.scss';
import {
  useCreditsAccountInfo,
  useGetUser,
  useManageCreditAccountPageRightPanel,
  useTeamInfo,
} from 'src/hooks';
import { getPriceFromNanos, getPriceWithCurrency } from 'src/utils';
import { Coins } from '@phosphor-icons/react';
import { SVG_SIZE_M } from 'src/constants';
import { AppRoutes, ManageCreditAccountRightSidePanelType } from 'src/types';
import { CancelAutoPay } from 'src/v2/pages/manageUsersInWorkspace/components/CreditsSection/components/CancelAutoPay';
import { useMemo } from 'react';
import { MIN_AUTO_PAY_THRESHOLD_VALUE } from 'src/v2/pages/subscription/components/CreditsCheckout';

export const CreditsSection = () => {
  const { isFreeTier, isTeamsTier, isOpenTier } = useGetUser();
  const { isOwner, isAdmin, isMember } = useTeamInfo();
  const {
    creditAccountInfo,
    isCreditAccountInfoLoading,
    isCreditAccountConfigured,
  } = useCreditsAccountInfo();
  const { changeActivePanelView } = useManageCreditAccountPageRightPanel();
  const showDiscountLabel = true;

  const handleUpdatePaymentMethodClick = () => {
    changeActivePanelView(
      ManageCreditAccountRightSidePanelType.UpdatePaymentMethod,
    );
  };

  const handleOpenManagePanel = () => {
    changeActivePanelView(ManageCreditAccountRightSidePanelType.ManageCredits);
  };

  const hasReloadedBalance = useMemo(
    () =>
      isCreditAccountConfigured &&
      creditAccountInfo &&
      creditAccountInfo?.reload_config?.auto_reload_enabled &&
      !isMember &&
      !isAdmin,
    [isCreditAccountConfigured, creditAccountInfo, isMember, isAdmin],
  );

  return (
    <div className={styles.root}>
      <div>
        <div className={styles.titleWrapper}>
          <h4 className={styles.title}>On-demand SuperNinja Credit Pack</h4>
          {hasReloadedBalance && (
            <span className={styles.enabledText}>Enabled</span>
          )}
        </div>
        <p className={styles.description}>
          {hasReloadedBalance
            ? 'Never run out of SuperNinja tasks again.'
            : 'Never run out of SuperNinja tasks again, from $50 to $10,000 Credit Pack options.'}
        </p>
        {!hasReloadedBalance && showDiscountLabel && (
          <span className={styles.discountText}>
            Buy More, Get More: Up to 10% Bonus Credits
          </span>
        )}
      </div>

      <div className={styles.description}>
        <p className={styles.subtitle}>How it works</p>

        <ul className={styles.list}>
          <li>
            When your plan’s SuperNinja mode usage hits its limit, credits are
            used automatically for additional tasks.
          </li>
          <li>Ninja Cline model access (coming soon) and API access</li>
        </ul>
      </div>

      <div className={styles.description}>
        <p className={styles.subtitle}>Auto-refill trigger</p>

        <p>
          Automatically refill your SuperNinja credits when your balance drops
          to ${MIN_AUTO_PAY_THRESHOLD_VALUE}.
        </p>
      </div>

      <div className={styles.infoWrapper}>
        <div className={styles.valueRow}>
          <span className={styles.label}>Available credit</span>
          <span className={styles.value}>
            {getPriceWithCurrency(
              creditAccountInfo
                ? getPriceFromNanos(creditAccountInfo?.balance_nanos)
                : 0,
            )}
          </span>
        </div>

        {hasReloadedBalance && (
          <>
            <div className={styles.valueRow}>
              <span className={styles.label}>Auto-pay threshold</span>
              <span className={styles.value}>
                {getPriceWithCurrency(
                  getPriceFromNanos(
                    creditAccountInfo?.reload_config
                      ?.reload_threshold_amount_nanos,
                  ),
                )}
              </span>
            </div>

            <div className={styles.valueRow}>
              <span className={styles.label}>Auto-pay amount</span>
              <span className={styles.value}>
                {getPriceWithCurrency(
                  getPriceFromNanos(
                    creditAccountInfo?.reload_config?.reload_amount_nanos,
                  ),
                )}
              </span>
            </div>
          </>
        )}
      </div>

      <div className={styles.buttonsRow}>
        {isFreeTier ? (
          <Button as="link" to={AppRoutes.PAYMENT} color="highlight">
            Purchase plans
          </Button>
        ) : (
          <Button
            className={styles.button}
            onClick={handleOpenManagePanel}
            color="highlight"
            tooltipContent={
              isTeamsTier && !isOwner
                ? 'Available for workspace owner'
                : isFreeTier
                  ? 'Please upgrade to purchase credits'
                  : ''
            }
            disabled={isTeamsTier && !isOwner}
          >
            {hasReloadedBalance ? 'Manage credits' : 'Enable'}
          </Button>
        )}

        {isCreditAccountConfigured &&
          !!creditAccountInfo &&
          creditAccountInfo.account_id &&
          creditAccountInfo.reload_config?.auto_reload_enabled &&
          !isMember &&
          !isAdmin && (
            <Button
              className={styles.manageCreditsButton}
              color="secondary-outline"
              disabled={isCreditAccountInfoLoading}
              onClick={handleUpdatePaymentMethodClick}
            >
              <Coins size={SVG_SIZE_M} />
              <span>Manage credit billing</span>
            </Button>
          )}

        {!isMember &&
          !isAdmin &&
          !isOpenTier &&
          !!creditAccountInfo &&
          creditAccountInfo.reload_config?.auto_reload_enabled && (
            <CancelAutoPay />
          )}
      </div>
    </div>
  );
};
